/**
 * Service layer for the Lists domain.
 * All functions communicate with the backend REST API via apiClient.
 */
import apiClient from "modules/main/services/apiClient";
import {
    endPointListItemSearch,
    endPointListItems,
    endPointListItem,
    endPointLists,
    endPointListDetail,
    endPointListExecute,
    endPointListExecution,
    endPointListCandidateSelect,
} from "modules/main/services/endpoints";
import { CandidateProduct, CreateListItemPayload, ItemSearchResult, List, ListDetail, ListExecution, ListExecutionStatus, ListItem, ListItemType, ResolvedListItem, SampleListItem } from "modules/lists/types/lists";

// ─── Backend response shapes (snake_case from the API) ──────────────────────

interface SampleListItemApiResponse {
    item_img_url: string | null;
}

interface ListApiResponse {
    id: string;
    name: string;
    description: string | null;
    sort_order: number;
    created_at: string;
    updated_at: string;
    item_count: number;
    sample_items: SampleListItemApiResponse[];
}

interface ListItemApiResponse {
    id: string;
    list_id: string;
    raw_description: string;
    catalog_product_id: string | null;
    linked_product_name: string | null;
    linked_product_sku: string | null;
    category_id: number | null;
    quantity: number;
    unit: string | null;
    notes: string | null;
    sort_order: number;
    type: string; // 'linked' | 'free'
}

interface ListDetailApiResponse extends Omit<ListApiResponse, "item_count"> {
    items: ListItemApiResponse[];
}

// ─── Mapper helpers ──────────────────────────────────────────────────────────

const mapList = (r: ListApiResponse): List => ({
    id: r.id,
    name: r.name,
    description: r.description,
    sortOrder: r.sort_order,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    itemCount: r.item_count,
    sampleItems: (r.sample_items ?? []).map((s): SampleListItem => ({ itemImgUrl: s.item_img_url })),
});

const mapListItem = (r: ListItemApiResponse): ListItem => ({
    id: r.id,
    listId: r.list_id,
    rawDescription: r.raw_description,
    catalogProductId: r.catalog_product_id,
    linkedProductName: r.linked_product_name,
    catalogProductSku: r.linked_product_sku,
    itemImgUrl: null,
    categoryId: r.category_id,
    quantity: r.quantity,
    unit: r.unit,
    notes: r.notes,
    sortOrder: r.sort_order,
    type: r.type === "linked" ? ListItemType.LINKED : ListItemType.FREE,
});

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Fetches all active lists for the current user, ordered by sort_order.
 * @returns Array of List objects.
 */
export const fetchLists = async (): Promise<List[]> => {
    const data = await apiClient.get<ListApiResponse[]>(endPointLists);
    return data.map(mapList);
};

/**
 * Fetches full detail of a list, including its items.
 * @param listId UUID of the list.
 * @returns ListDetail with items array.
 */
export const fetchListDetail = async (listId: string): Promise<ListDetail> => {
    const data = await apiClient.get<ListDetailApiResponse>(endPointListDetail(listId));
    return {
        ...mapList({ ...data, item_count: data.items.length }),
        items: data.items.map(mapListItem),
    };
};

/**
 * Creates a new list for the current user.
 * @param name List name (max 120 chars).
 * @param description Optional description (max 255 chars).
 * @returns The newly created List.
 */
export const createList = async (name: string, description: string | null): Promise<List> => {
    const data = await apiClient.post<ListApiResponse>(endPointLists, {
        name,
        description: description || null,
    });
    return mapList(data);
};

/**
 * Updates the name and/or description of an existing list.
 * @param listId UUID of the list.
 * @param payload Fields to update (all optional).
 * @returns Updated List.
 */
export const updateList = async (
    listId: string,
    payload: Partial<{ name: string; description: string | null }>,
): Promise<List> => {
    const data = await apiClient.put<ListApiResponse>(endPointListDetail(listId), payload);
    return mapList(data);
};

/**
 * Soft-deletes a list.
 * @param listId UUID of the list.
 */
export const deleteList = async (listId: string): Promise<void> => {
    await apiClient.delete<void>(endPointListDetail(listId));
};

// ─── Item search API response shape ─────────────────────────────────────────

interface ItemSearchResultApiResponse {
    id: string;
    s_id: number;
    name: string;
    brand: string;
    image_url: string | null;
    price: number | null;
    unit: string | null;
    total: number | null;
    category_id: number;
    parsed_quantity: number;
    parsed_unit: string | null;
    hashed_sku: string | null;
}

interface ItemSearchApiResponse {
    search_term: string;
    parsed_quantity: number;
    parsed_unit: string | null;
    category_id: number | null;
    attribution_token: string;
    results: ItemSearchResultApiResponse[];
}

const mapItemSearchResult = (r: ItemSearchResultApiResponse): ItemSearchResult => ({
    id: r.id,
    s_id:r.s_id,
    name: r.name,
    brand: r.brand,
    imageUrl: r.image_url,
    price: r.price,
    unit: r.unit,
    total: r.total,
    categoryId: r.category_id,
    parsedQuantity: r.parsed_quantity,
    parsedUnit: r.parsed_unit,
    sku: r.hashed_sku,
});

/**
 * Searches for product candidates matching the user's raw description.
 * @param q Raw item description typed by the user.
 * @returns Parsed metadata + product candidates.
 */
export const searchItem = async (
    q: string,
    signal?: AbortSignal,
    skipParse?: boolean,
): Promise<{ searchTerm: string; parsedQuantity: number; parsedUnit: string | null; categoryId: number | null; results: ItemSearchResult[]; attribution_token: string }> => {
    const data = await apiClient.get<ItemSearchApiResponse>(endPointListItemSearch(q, skipParse), { signal });
    return {
        searchTerm: data.search_term,
        parsedQuantity: data.parsed_quantity,
        parsedUnit: data.parsed_unit,
        categoryId: data.category_id,
        results: data.results.map(mapItemSearchResult),
        attribution_token: data.attribution_token,
    };
};

/**
 * Adds a new item to an existing list.
 * @param listId UUID of the parent list.
 * @param payload Item creation payload.
 * @returns The newly created ListItem.
 */
/**
 * Updates an existing list item.
 * @param listId UUID of the parent list.
 * @param itemId UUID of the item to update.
 * @param payload Updated item fields.
 * @param signal Optional AbortSignal for request cancellation.
 * @returns The updated ListItem.
 */
export const updateItem = async (
    listId: string,
    itemId: string,
    payload: CreateListItemPayload,
    signal?: AbortSignal,
): Promise<ListItem> => {
    const data = await apiClient.put<ListItemApiResponse>(
        endPointListItem(listId, itemId),
        {
            raw_description: payload.rawDescription,
            catalog_product_id: payload.catalogProductId ?? null,
            catalog_product_sku: payload.catalogProductSku ?? null,
            category_id: payload.categoryId ?? null,
            quantity: payload.quantity,
            unit: payload.unit ?? null,
            notes: payload.notes ?? null,
        },
        { signal },
    );
    return mapListItem(data);
};

/**
 * Adds a new item to an existing list.
 * @param listId UUID of the parent list.
 * @param payload Item creation payload.
 * @returns The newly created ListItem.
 */
export const addItem = async (
    listId: string,
    payload: CreateListItemPayload,
): Promise<ListItem> => {
    const data = await apiClient.post<ListItemApiResponse>(endPointListItems(listId), {
        raw_description: payload.rawDescription,
        catalog_product_id: payload.catalogProductId ?? null,
        catalog_product_sku: payload.catalogProductSku ?? null,
        category_id: payload.categoryId ?? null,
        quantity: payload.quantity,
        unit: payload.unit ?? null,
        notes: payload.notes ?? null,
    });
    return mapListItem(data);
};

/**
 * Soft-deletes an item from a list.
 * @param listId UUID of the parent list.
 * @param itemId UUID of the item to delete.
 */
export const deleteItem = async (listId: string, itemId: string): Promise<void> => {
    await apiClient.delete<void>(endPointListItem(listId, itemId));
};

// ─── Execution API response shapes ──────────────────────────────────────────

interface CandidateProductApiResponse {
    id: string;
    catalog_product_id: number;
    name: string;
    brand: string | null;
    image_url: string | null;
    price: number | null;
    unit: string | null;
    confidence_score: number;
    rank: number;
    selected: boolean;
    sku?: string | null;
}

/**
 * Flat resolved item response from the API.
 * Replaces the nested ItemWithCandidatesApiResponse structure.
 */
interface ResolvedListItemApiResponse {
    id: string;
    list_id: string;
    raw_description: string;
    catalog_product_id: string | null;
    name: string | null;
    brand: string | null;
    image_url: string | null;
    price: number | null;
    category_id: number | null;
    quantity: number;
    unit: string | null;
    notes: string | null;
    sort_order: number;
    type: string;
    sku: string | null;
    confidence_score: number | null;
    selected_candidate_id: string | null;
}

interface ListExecutionApiResponse {
    id: string;
    list_id: string;
    status: string;
    items_with_candidates: ResolvedListItemApiResponse[];
    temporary_items_with_candidates?: ResolvedListItemApiResponse[];
    cart_id: string | null;
    created_at: string;
}

const mapResolvedListItem = (r: ResolvedListItemApiResponse): ResolvedListItem => ({
    id: r.id,
    listId: r.list_id,
    rawDescription: r.raw_description,
    catalogProductId: r.catalog_product_id,
    linkedProductName: r.name,
    catalogProductSku: r.sku,
    itemImgUrl: r.image_url,
    categoryId: r.category_id,
    quantity: r.quantity,
    unit: r.unit,
    notes: r.notes,
    sortOrder: r.sort_order,
    type: r.type === "linked" ? ListItemType.LINKED : ListItemType.FREE,
    name: r.name,
    brand: r.brand ?? null,
    imageUrl: r.image_url,
    price: r.price,
    sku: r.sku,
    confidenceScore: r.confidence_score,
    selectedCandidateId: r.selected_candidate_id,
});

const mapListExecution = (r: ListExecutionApiResponse): ListExecution => ({
    id: r.id,
    listId: r.list_id,
    status: r.status as ListExecutionStatus,
    itemsWithCandidates: r.items_with_candidates.map(mapResolvedListItem),
    temporaryItemsWithCandidates: (r.temporary_items_with_candidates ?? []).map(mapResolvedListItem),
    cartId: r.cart_id,
});

// ─── Execution public API ────────────────────────────────────────────────────

/**
 * Creates a new list execution and fires the LLM resolution synchronously.
 * Backend guarantees status 'draft' and candidates in the first response.
 * @param listId UUID of the list to execute.
 * @param locationId User's selected location ID for price hydration.
 * @param excludedItemIds Optional array of item IDs to exclude from execution.
 * @param itemQuantities Optional map of item ID to quantity override (item ID → quantity).
 * @param temporaryItems Optional array of temporary items to include in execution.
 * @returns ListExecution with resolved candidates and status 'draft'.
 */
export const executeList = async (
    listId: string,
    excludedItemIds?: string[],
    itemQuantities?: Record<string, number>,
    temporaryItems?: { tempKey: string; rawDescription: string; quantity: number }[],
): Promise<ListExecution> => {
    const data = await apiClient.post<ListExecutionApiResponse>(
        endPointListExecute(listId),
        {
            excluded_item_ids: excludedItemIds ?? [],
            item_quantities: itemQuantities
                ? Object.entries(itemQuantities).map(([item_id, quantity]) => ({ item_id, quantity }))
                : [],
            temporary_items: (temporaryItems ?? []).map((t) => ({
                temp_key: t.tempKey,
                raw_description: t.rawDescription,
                quantity: t.quantity,
            })),
        },
    );
    return mapListExecution(data);
};

/**
 * Fetches the current state of a list execution.
 * Retained as a future refresh/resume utility (e.g., manual refresh of resolution state).
 * @param executionId UUID of the execution.
 * @returns ListExecution with up-to-date candidates.
 */
export const getListExecution = async (
    executionId: string,
): Promise<ListExecution> => {
    const data = await apiClient.get<ListExecutionApiResponse>(
        endPointListExecution(executionId),
    );
    return mapListExecution(data);
};

/**
 * Changes the selected candidate for a specific list item inside an execution.
 * @param executionId UUID of the execution.
 * @param listItemId UUID of the list item.
 * @param candidateId UUID of the candidate to select.
 */
export const selectCandidate = async (
    executionId: string,
    listItemId: string,
    candidateId: string,
): Promise<void> => {
    await apiClient.patch<void>(endPointListCandidateSelect(executionId, listItemId), {
        candidate_id: candidateId,
    });
};
